import { isRecord } from '@destera/core';
import type { WidgetBrand } from './config.ts';

/**
 * İşletmenin markasının sunucudan alınması ve tarayıcıda saklanması.
 *
 * Dosya adı `brand.ts` DEĞİL, çünkü o ad zaten alınmış ve başka bir şeyi anlatıyor: bizim kendi
 * marka bağlantımız. İkisini aynı dosyaya koymak, "marka" sözcüğünün iki ayrı anlamını tek yerde
 * toplamak olurdu.
 *
 * **Önce ÇİZ, sonra düzelt.** Balon saklanan markayla (yoksa ürün varsayılanıyla) anında
 * çiziliyor; taze marka arka planda isteniyor ve bir sonraki sayfa açılışını hazırlıyor. Markayı
 * bekleyip sonra çizmek, en hafif widget iddiasını bozardı — ziyaretçi bir ağ turu boyunca
 * hiçbir şey görmezdi.
 *
 * **Tazelik penceresi burada TUTULMUYOR.** İlk hâli kendi zaman damgasını saklıyordu ve bu
 * gereksizdi: sunucu yanıtı `max-age=300` ile geliyor, yani beş dakika içindeki ikinci istek
 * tarayıcının kendi önbelleğinden dönüyor ve ağa hiç çıkmıyor. Aynı işi iki yerde yapmak hem
 * yükleyiciye bayt ekliyordu (boyut bütçesi aşıldı) hem de iki pencerenin bir gün ayrışması
 * demekti.
 *
 * **Doğrulanan tek alan RENK,** çünkü CSS'e HAM giren tek değer o: metinler Preact'in kaçışlı
 * metin yolundan geçiyor, balonun etiketi `textContent` ile yazılıyor. Denetim OKUMADA da
 * yapılıyor, yalnız yazmada değil — depoya başka bir yerden bozuk değer girmiş olabilir.
 *
 * **Stil tokenları (köşe, gölge, yazı tipi) BURADA doğrulanmıyor ve gerekmiyor:** onlar CSS'e
 * ham girmiyor, tema tablosunda ARANIYOR (`shared/theme.ts`) ve tanınmayan ad varsayılana
 * düşüyor. Ad listesini buraya da kopyalamak, aynı listeyi iki yerde tutmak ve ölçeğe bir ad
 * eklendiği gün eski widget'ların markayı TAMAMEN reddetmesi demekti — ziyaretçi o sayfada
 * renksiz bir balon görürdü.
 */

const KEY = 'destera.appearance';

/** `config.ts` ile aynı kural: renk CSS'e giriyor ve yalnız bu biçim güvenli. */
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const isBrand = (value: unknown): value is WidgetBrand => {
  if (!isRecord(value)) return false;
  const accent = value['accent'];
  return typeof accent === 'string' && HEX_COLOR.test(accent);
};

/**
 * Saklanan marka.
 *
 * Depo erişimi HER ZAMAN sarmalanıyor: gizli sekmede ve site verisi kapalıyken `localStorage`
 * okumak İSTİSNA fırlatıyor ve yakalanmazsa widget hiç çizilmez. Marka bir kolaylık; onun
 * yüzünden sohbetin açılmaması kabul edilemez.
 */
export function storedBrand(workspaceId: string): WidgetBrand | undefined {
  try {
    const raw = globalThis.localStorage.getItem(`${KEY}.${workspaceId}`);
    const brand: unknown = raw === null ? undefined : JSON.parse(raw);
    return isBrand(brand) ? brand : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Markayı sunucudan alıp saklar ve DÖNDÜRÜR: sonraki açılış depodan okur, ilk açılış ise
 * balonu taze markayla yerinde düzeltir (bkz. yükleyici). 2026-10-08'e kadar sonuç yalnız
 * saklanıyordu ve işletmenin sitesine ilk gelen herkes mor balon, "Asistan" başlığı ve ürünün
 * karşılamasını görüyordu — markanın görünmediği tek ziyaret, ilk izlenimin kendisiydi.
 *
 * Her hata sessizce yutuluyor: marka alınamadığında yapılacak doğru şey hiçbir şey yapmamak,
 * widget varsayılan rengiyle çalışmaya devam ediyor. Konsola yazmak da hayır — sitenin sahibinin
 * hatası değil.
 */
export async function refreshBrand(
  apiBase: string,
  workspaceId: string,
): Promise<WidgetBrand | undefined> {
  try {
    const response = await fetch(`${apiBase}/v1/widget/brand/${workspaceId}`);
    const brand: unknown = response.ok ? await response.json() : undefined;
    if (!isBrand(brand)) return undefined;
    try {
      globalThis.localStorage.setItem(`${KEY}.${workspaceId}`, JSON.stringify(brand));
    } catch {
      /* Depo yoksa marka yine uygulanıyor, yalnız her açılışta yeniden isteniyor. */
    }
    return brand;
  } catch {
    return undefined;
  }
}
